import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Warehouse, WarehouseCell } from 'src/typings';
import { CurrentUserService } from '../user/user.service';
import { WarehouseService } from '../warehouse/warehouse.service';
import { extractApiErrorMessage } from '../shared/utils/extract-api-error-message.util';
import { OrderStagingService } from './order-staging.service';

@Component({
  selector: 'app-order-picking',
  templateUrl: './order-picking.component.html',
  styleUrls: ['./order-picking.component.scss']
})
export class OrderPickingComponent implements OnInit {
  @ViewChild('barcodeInput') barcodeInput?: ElementRef<HTMLInputElement>;

  warehouses: Warehouse[] = [];

  warehouseId = 0;

  tasks: any[] = [];

  task: any;

  selectedItem: any;

  handling: any;

  destinationId: number | null = null;

  barcode = '';

  quantity = 1;

  returnToSource = false;

  loading = false;

  error = '';

  lastAction = '';

  readonly scanQueue: string[] = [];

  private scanBuffer = '';

  private drainingScans = false;

  constructor(
    private readonly staging: OrderStagingService,
    private readonly warehouse: WarehouseService,
    private readonly user: CurrentUserService,
    private readonly route: ActivatedRoute
  ) {}

  async ngOnInit(): Promise<void> {
    await this.run(async () => {
      this.warehouses = await firstValueFrom(this.warehouse.getAllList());
      this.warehouseId = this.user.user?.defaultWarehouse?.id || this.warehouses[0]?.id || 0;
      await this.refresh();
      const orderId = Number(this.route.snapshot.queryParamMap.get('orderId'));
      if (orderId && !this.handling) await this.openOrder(`ORDER:${orderId}`);
    });
  }

  get scope(): string {
    return `picking-actor-${this.user.user?.id || 0}`;
  }

  get pendingRequest(): boolean {
    return this.staging.hasPending(this.scope);
  }

  get addressed(): boolean {
    return Number(this.task?.workflow_version) === 1;
  }

  get orderId(): number {
    return Number(this.task?.orderId || this.task?.order_id || 0);
  }

  get placements(): any[] {
    return this.task?.order_placements || [];
  }

  get hasRemaining(): boolean {
    return (this.task?.items || []).some((row: any) => Number(row.remaining_quantity) > 0);
  }

  get prompt(): string {
    if (this.handling)
      return this.returnToSource
        ? 'Сканируйте исходную ячейку для возврата'
        : 'Положите товар и сканируйте ячейку заказа';
    if (this.selectedItem) return 'Возьмите выбранный товар и сканируйте исходную ячейку';
    return 'Сканируйте ORDER:номер или выберите задачу';
  }

  async changeWarehouse(): Promise<void> {
    await this.run(async () => {
      this.task = undefined;
      this.selectedItem = undefined;
      await this.refresh();
    });
  }

  async refresh(): Promise<void> {
    if (!this.warehouseId) return;
    const [tasks, handlings] = await Promise.all([
      this.staging.pickTasks(this.warehouseId),
      this.staging.handlings(this.warehouseId)
    ]);
    this.tasks = Array.isArray(tasks) ? tasks : tasks.data || [];
    this.handling = handlings.find((row: any) => row.status === 'IN_HANDS');
    if (this.handling) this.task = await this.staging.joinTask(this.handling.task_id);
    else if (this.task?.id) this.task = await this.staging.joinTask(this.task.id);
    if (this.selectedItem)
      this.selectedItem = this.task?.items?.find(
        (row: any) => row.id === this.selectedItem.id && row.remaining_quantity > 0
      );
    if (!this.placements.some((row) => row.cell_id === this.destinationId))
      this.destinationId = this.placements[0]?.cell_id || null;
  }

  async selectTask(task: any): Promise<void> {
    if (this.handling || this.pendingRequest) return;
    await this.run(async () => {
      this.task = await this.staging.joinTask(task.id);
      this.selectedItem = undefined;
      this.destinationId = this.placements[0]?.cell_id || null;
    });
  }

  chooseItem(item: any): void {
    if (this.loading || this.handling || this.pendingRequest || item.remaining_quantity <= 0)
      return;
    this.selectedItem = item;
    this.quantity = 1;
    this.barcode = '';
    this.focus();
  }

  async scan(): Promise<void> {
    if (!this.barcode.trim()) return;
    const barcode = this.barcode.trim();
    this.barcode = '';
    this.scanQueue.push(barcode);
    await this.drainScanQueue();
  }

  @HostListener('window:keydown', ['$event'])
  onWindowKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    if (
      target &&
      ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) &&
      !(target as HTMLInputElement).readOnly
    )
      return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.key === 'Enter') {
      if (this.scanBuffer) {
        event.preventDefault();
        this.scanQueue.push(this.scanBuffer);
        this.scanBuffer = '';
        void this.drainScanQueue();
      }
    } else if (event.key.length === 1) {
      this.scanBuffer += event.key;
      event.preventDefault();
    }
  }

  private async drainScanQueue(): Promise<void> {
    if (this.drainingScans || this.loading || this.pendingRequest) return;
    this.drainingScans = true;
    try {
      while (this.scanQueue.length && !this.loading && !this.pendingRequest)
        await this.processScan(this.scanQueue.shift()!);
    } finally {
      this.drainingScans = false;
    }
  }

  private async processScan(barcode: string): Promise<void> {
    await this.run(async () => {
      if (!this.handling && /^ORDER:\d+$/i.test(barcode)) {
        await this.openOrder(barcode);
        return;
      }
      if (!barcode.startsWith('!'))
        throw new Error('Нужен штрихкод ячейки !КОД. Сначала выберите товар.');
      const cell = await this.resolveCell(barcode);
      if (this.handling) {
        const expected = Number(
          this.returnToSource ? this.handling.source_cell_id : this.handling.destination_cell_id
        );
        if (cell.id !== expected)
          throw new Error(
            'Это другая ячейка. Сканируйте адрес, указанный для текущего товара в руках.'
          );
        const handling = this.handling;
        await this.staging.command(
          this.scope,
          `/order-staging/handlings/${handling.id}/${this.returnToSource ? 'return' : 'place'}`,
          { cellId: cell.id }
        );
        this.lastAction = `${handling.quantity} шт. ${
          this.returnToSource ? 'возвращено' : 'размещено'
        } → ${cell.code}`;
        this.returnToSource = false;
      } else {
        if (!this.selectedItem)
          throw new Error('Выберите строку товара перед сканированием исходной ячейки.');
        if (
          !Number.isFinite(Number(this.quantity)) ||
          this.quantity <= 0 ||
          this.quantity > this.selectedItem.remaining_quantity
        )
          throw new Error('Проверьте количество к отбору.');
        if (this.addressed) {
          if (!this.destinationId)
            throw new Error('Сначала добавьте место заказу и выберите назначение.');
          this.handling = await this.staging.command(
            this.scope,
            `/order-staging/tasks/${this.task.id}/take`,
            {
              itemId: this.selectedItem.id,
              sourceCellId: cell.id,
              destinationCellId: this.destinationId,
              quantity: Number(this.quantity)
            }
          );
          this.lastAction = `${this.quantity} шт. взято из ${cell.code}. Товар в руках.`;
        } else {
          this.task = await this.staging.command(
            this.scope,
            `/mobile/warehouse/v1/pick-tasks/${this.task.id}/pick`,
            {
              itemId: this.selectedItem.id,
              cellId: cell.id,
              qty: Number(this.quantity)
            },
            'PUT'
          );
          this.lastAction = `${this.quantity} шт. отобрано по прежней задаче.`;
        }
      }
      await this.refresh();
    });
  }

  async retry(): Promise<void> {
    await this.run(async () => {
      await this.staging.retry(this.scope);
      await this.refresh();
    });
  }

  async finish(): Promise<void> {
    if (!this.task || this.handling || this.pendingRequest) return;
    await this.run(async () => {
      await this.staging.command(
        this.scope,
        `/mobile/warehouse/v1/pick-tasks/${this.task.id}/finish`,
        {},
        'PUT'
      );
      this.task = undefined;
      this.selectedItem = undefined;
      await this.refresh();
      this.lastAction = 'Сборка завершена. Размещённую часть заказа можно отгружать.';
    });
  }

  async reload(): Promise<void> {
    await this.run(() => this.refresh());
  }

  private async openOrder(barcode: string): Promise<void> {
    const task = await this.staging.taskByOrder(barcode);
    this.task = await this.staging.joinTask(task.id);
    this.warehouseId = Number(this.task.warehouseId);
    this.selectedItem = undefined;
    this.destinationId = this.placements[0]?.cell_id || null;
    await this.refresh();
  }

  private async resolveCell(barcode: string): Promise<WarehouseCell> {
    const code = barcode.slice(1).trim().toUpperCase();
    const cells = await firstValueFrom(
      this.warehouse.searchCells({ warehouseId: this.warehouseId, code })
    );
    const cell = cells.find((row) => row.code.toUpperCase() === code && row.isActive);
    if (!cell) throw new Error('Ячейка не найдена на выбранном складе.');
    return cell;
  }

  private async run(action: () => Promise<any>): Promise<void> {
    if (this.loading) return;
    this.loading = true;
    this.error = '';
    try {
      await action();
    } catch (error) {
      this.error = extractApiErrorMessage((error as any)?.error || error, 'Операция не выполнена');
    } finally {
      this.loading = false;
      this.focus();
      void this.drainScanQueue();
    }
  }

  private focus(): void {
    setTimeout(() => this.barcodeInput?.nativeElement.focus());
  }
}
